import { z } from 'zod';
import type {
  ConversationItemResponse,
  HubItemResponse,
  MessageItemResponse,
  AvailableTaskHub,
} from './chat';
import type { NavigationCounts } from './live-updates';
import type { UserDataResponse } from './users';
import type { HubAlert } from './hub-alerts';
import type { PlatformNotice } from './notices';

// ---------------------------------------------------------------------------
// GET /users/me/bootstrap — query
// ---------------------------------------------------------------------------

/**
 * Bootstrap query — optional active URL hints resolved by the RSC.
 *
 * Route-level RSC parses the current URL (e.g. /chat/[hubId]/[conversationId])
 * and passes hints to the backend so the snapshot can pre-load the active
 * conversation's first-page messages.
 */
export const bootstrapQuery = z.object({
  active_hub_id: z.string().uuid().optional(),
  active_conversation_id: z.string().uuid().optional(),
  active_nav: z.enum(['chat', 'task', 'support']).optional(),
});

export type BootstrapQuery = z.infer<typeof bootstrapQuery>;

// ---------------------------------------------------------------------------
// WebSocket — request_snapshot_chunk (cursor-paged follow-up)
// ---------------------------------------------------------------------------

export const requestSnapshotChunkMessage = z.object({
  type: z.literal('request_snapshot_chunk'),
  cursor: z.string(),
});

export type RequestSnapshotChunkMessage = z.infer<typeof requestSnapshotChunkMessage>;

// ---------------------------------------------------------------------------
// Response shape — shared between HTTP (/bootstrap) and WS (session_snapshot)
// ---------------------------------------------------------------------------

/**
 * Per-nav conversation slice. Arrays so the RSC-serialized payload crosses
 * the Next.js boundary without Map serialization issues; client hydrator
 * feeds these into Zustand Map setters.
 */
export interface SessionSnapshotConversations {
  chat: ConversationItemResponse[];
  task: ConversationItemResponse[];
  support: ConversationItemResponse[];
}

/**
 * Active conversation pre-loaded by the RSC. Present only when the URL
 * resolved to a specific conversation the user has access to.
 */
export interface SessionSnapshotActiveConversation {
  conversation: ConversationItemResponse;
  messages: MessageItemResponse[];
}

/**
 * Per-user session snapshot. Built once in
 * `workers/backend/src/live-updates/session-snapshot.ts` and consumed by both:
 * - HTTP: `GET /api/users/me/bootstrap` (RSC first paint)
 * - WS:   `LiveUpdateSession.getSessionSnapshot()` RPC (on auth_ok)
 */
export interface SessionSnapshot {
  user: UserDataResponse;
  hubs: HubItemResponse[];
  available_task_hubs: AvailableTaskHub[];
  conversations: SessionSnapshotConversations;
  active_conversation: SessionSnapshotActiveConversation | null;
  nav_counts: NavigationCounts;
  /**
   * Maps hub_id → the viewer's hub-scoped user_id (PK of HubDO's local `user`
   * table) for each hub where the viewer is an end user. Optional: a Worker
   * that predates this field omits it. Hubs with no resolvable
   * `hub_user.user_id` are absent from the map.
   */
  hub_user_ids_by_hub?: Record<string, string>;
  /**
   * Hub ids where the viewer has team/admin (support) access — i.e. the
   * `teamHubIds` set the builder uses for the support scope. Drives the live
   * update path's per-recipient `source_navs` inference (a recipient adds
   * `'support'` membership for a conversation whose hub is in this set).
   * Optional: a Worker that predates this field omits it.
   */
  support_hub_ids?: string[];
  /**
   * Active Status & Notices hub-health alerts across the hubs the viewer admins
   * (hub-local + org-derived union, body-free, each stamped with `hub_id`). Seeds the
   * app-shell banner + the cross-hub aggregate without a separate fetch — "always
   * live". Optional: a Worker that predates this field omits it.
   *
   * NOTE: a shared org credential backing M connections in one hub yields M entries
   * with the SAME `alert_id` (distinct `connection_id`) — the fan-out that surfaces the
   * one org alert against each linking connection. Consumers MUST key by the composite
   * `${alert_id}:${connection_id}` (not `alert_id` alone), as the Status tab does.
   */
  active_alerts?: HubAlert[];
  /**
   * Active platform broadcasts (Status & Notices feed 2) visible to this viewer —
   * `global`-scoped, within their display window, minus the ones they've acknowledged.
   * The same set `GET /api/notices` returns; seeds the notice banner + the account
   * aggregate without a separate fetch ("always live"). Optional: a Worker that
   * predates this field omits it. Consumers key by `notice_id`.
   */
  active_notices?: PlatformNotice[];
  /**
   * Serialized size ceiling guard — when the full payload exceeds the
   * snapshot size budget, the builder paginates via cursor. `null` = done.
   */
  cursor: string | null;
}

export interface BootstrapResponse {
  success: true;
  data: SessionSnapshot;
}

// ---------------------------------------------------------------------------
// WebSocket — session_snapshot message (emitted after auth_ok)
// ---------------------------------------------------------------------------

export interface SessionSnapshotMessage {
  type: 'session_snapshot';
  payload: SessionSnapshot;
}
